<!--
	@author bluefirex
-->
<template>
	<div class="acquisit-condition-tester default-wrapper">
		<StateInspectorHeader class="header">
			<template #default>
				<h2>Condition Tester</h2>
			</template>
		</StateInspectorHeader>
		
		<div class="tester-form">
			<div class="form-with-results">
				<div class="form">
					<acquisit-textarea v-model="conditionString"
									   theme="dark"
					                   color="blue"
									   label="Condition" />
					
					<acquisit-button color-container="#080a0e"
									 color="blue"
									 @click="run"
					                 icon="acq/send"
					                 icon-position="top"
									 label="Run" />
				</div>
			
				<div class="result">
					<h5>Result:</h5>
					
					<span class="true" v-if="result === true">
						true
					</span>
					
					<span class="false" v-else-if="result === false">
						false
					</span>
					
					<span class="null" v-else-if="result === undefined">
						–
					</span>
					
					<span class="error" v-else>
						{{ result }}
					</span>
				</div>
			</div>
			
			<div class="condition-debug" v-if="lastCondition">
				<StateInspectorJsonView :json="lastCondition" />
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, toValue } from 'vue'
	import { useInjectedComponentsStore } from '@broker-as/acquisit-ui-vue/helpers'
	import { evaluateCondition, parseStringIntoExpression } from '@broker-as/acquisit-ui-vue/expressions'
	import type { EvaluationContext, Expression } from '@broker-as/acquisit-ui-vue/expressions'
	import StateInspectorHeader from '@ui/dev/StateInspectorHeader.vue'
	import { useProtocolStore } from '@/stores/protocol'
	import { usePersonsStore } from '@/stores/persons'
	import { useProductsStore } from '@/stores/products'
	import StateInspectorJsonView from '@ui/dev/StateInspectorJsonView.vue'
	
	const conditionString = ref('')
	const result = ref<any>(undefined)
	
	const protocolStore = useProtocolStore()
	const personsStore = usePersonsStore()
	const productsStore = useProductsStore()
	
	const lastCondition = ref<Expression|undefined>()
	
	const injectedComponentsStore = useInjectedComponentsStore()
	
	const run = () => {
		try {
			lastCondition.value = parseStringIntoExpression(conditionString.value) ?? undefined
			
			const context: EvaluationContext = {
				componentsByUID: toValue(injectedComponentsStore.byUID),
				pagesByUID: protocolStore.pages_by_uid,
				personsByID: personsStore.by_id,
				productsByUID: productsStore.by_uid,
				throwErrors: true,
				scope: {}
			}
			
			result.value = evaluateCondition(lastCondition.value!, context)
		} catch (e: any) {
			result.value = e.message
			console.error(e)
		}
	}
</script>

<style lang="scss">
	@use 'sass:color';
	@use '@/assets/mixins.scss' as m;
	
	.acquisit-condition-tester {
		container: condition-tester / inline-size;
		display: flex;
		flex-direction: column;
		flex: 1;
		
		.tester-form {
			flex: 1;
		}
		
		.form {
			display: flex;
			align-items: stretch;
			gap: 16px;
			padding: 0px;
			
			.acquisit-textarea {
				flex: 1;
				
				textarea {
					background: transparent;
					color: #fff;
					font-family: m.font(monospace);
					font-size: 1.0rem;
				}
				
				.label {
					background: #000;
				}
			}
			
			.acquisit-button {
				display: block;
				
				.wrapper {
					box-sizing: border-box;
					height: 100%;
				}
			}
		}
		
		.result {
			display: flex;
			align-items: center;
			gap: 8px;
			margin-top: 24px;
			
			h5 {
				font-weight: 600;
			}
			
			.true {
				background: color.adjust(m.color("green"), $lightness: -10%, $space: hsl);
			}
			
			.false {
				background: color.adjust(m.color("red"), $lightness: -10%, $space: hsl);
			}
			
			.true,
			.false,
			.null {
				display: block;
				padding: 2px 8px;
				border-radius: 6px;
				font-weight: 600;
			}
		}
		
		.condition-debug {
			margin-top: 24px;
		}
		
		@container condition-tester (min-width: 960px) {
			
			.tester-form {
				display: flex;
				gap: 24px;
				flex: 1;
				
				.form-with-results {
					flex: 1;
					overflow-y: auto;
				}
				
				.condition-debug {
					flex: 1;
					background: rgba(#000, 1.0);
					margin-top: 0px;
					padding: 16px;
					border-radius: 6px;
				}
			}
		}
	}
	
	.acquisit-dev-state-inspector.collapsed .acquisit-condition-tester {
		
		.acquisit-state-inspector-header {
			display: none;
		}
	}
</style>